import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, CreditCard, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useLanguage } from '@/contexts/LanguageContext';
import { CURRENCY_SYMBOLS } from '@/lib/currencyConversion';
import {
  type RecurringExpense,
  type RecurringFrequency,
  getCategoryIcon,
  getFrequencyShort,
  getDueDateLabel,
  getDueUrgencyClass,
  calculateMonthlyTotal,
} from '@/lib/recurringCalculations';
import { RecurringExpenseDialog } from '@/components/Recurring/RecurringExpenseDialog';
import { expenseSchema } from '@/lib/expenseValidation';

const CATEGORIES = ['Food', 'Transport', 'Shopping', 'Fun', 'Health', 'Housing', 'Bills/Other'];
const CURRENCIES = ['EUR', 'USD', 'GBP', 'UAH', 'CAD', 'AUD', 'BRL', 'JPY', 'CHF', 'CNY'];

interface Template {
  id: string;
  name: string;
  amount: number;
  merchant: string;
  category: string;
  currency: string;
  note: string | null;
}

export const SavedExpensesManager = () => {
  const { toast } = useToast();
  const { t } = useLanguage();
  
  const [activeTab, setActiveTab] = useState('bills');
  const [currency, setCurrency] = useState('EUR');
  const [isLoading, setIsLoading] = useState(true);
  
  // Recurring expenses state
  const [expenses, setExpenses] = useState<RecurringExpense[]>([]);
  const [recurringDialogOpen, setRecurringDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<RecurringExpense | null>(null);
  const [deleteRecurringOpen, setDeleteRecurringOpen] = useState(false);
  const [deletingExpense, setDeletingExpense] = useState<RecurringExpense | null>(null);
  
  // Templates state
  const [templates, setTemplates] = useState<Template[]>([]);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [deleteTemplateOpen, setDeleteTemplateOpen] = useState(false);
  const [deletingTemplate, setDeletingTemplate] = useState<Template | null>(null);
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    amount: '',
    merchant: '',
    category: 'Food',
    currency: 'EUR',
    note: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get user's currency
      const { data: settings } = await supabase
        .from('budget_settings')
        .select('currency')
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (settings?.currency) {
        setCurrency(settings.currency);
        setNewTemplate(prev => ({ ...prev, currency: settings.currency }));
      }

      // Get recurring expenses
      const { data: recurringData } = await supabase
        .from('recurring_expenses')
        .select('*')
        .eq('user_id', user.id)
        .order('next_due_date', { ascending: true });
      
      const typedData = (recurringData || []).map(item => ({
        ...item,
        frequency: item.frequency as RecurringFrequency,
      }));
      setExpenses(typedData);

      // Get templates
      const { data: templateData } = await supabase
        .from('expense_templates')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      
      setTemplates(templateData || []);
    } catch (error) {
      console.error('Load data error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Recurring expense handlers
  const handleEditRecurring = (expense: RecurringExpense) => {
    setEditingExpense(expense);
    setRecurringDialogOpen(true);
  };

  const handleAddRecurring = () => {
    setEditingExpense(null);
    setRecurringDialogOpen(true);
  };

  const handleDeleteRecurring = async () => {
    if (!deletingExpense) return;
    try {
      await supabase.from('recurring_expenses').delete().eq('id', deletingExpense.id);
      toast({ description: `"${deletingExpense.name}" removed` });
      loadData();
    } catch (error) {
      toast({ title: t('common.error'), description: 'Failed to delete', variant: 'destructive' });
    } finally {
      setDeleteRecurringOpen(false);
      setDeletingExpense(null);
    }
  };

  // Template handlers
  const handleCreateTemplate = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const validated = expenseSchema.parse({
        amount: parseFloat(newTemplate.amount),
        merchant: newTemplate.merchant,
        category: newTemplate.category,
        note: newTemplate.note || null,
        expense_date: new Date().toISOString().split('T')[0],
        currency: newTemplate.currency,
      });

      await supabase.from('expense_templates').insert({
        user_id: user.id,
        name: newTemplate.name,
        amount: validated.amount,
        merchant: validated.merchant,
        category: validated.category,
        currency: validated.currency,
        note: validated.note,
      });

      toast({
        title: t('templates.created'),
        description: t('templates.readyToUse', { name: newTemplate.name }),
      });

      setNewTemplate({ name: '', amount: '', merchant: '', category: 'Food', currency, note: '' });
      setTemplateDialogOpen(false);
      loadData();
    } catch (error: any) {
      toast({ title: t('common.error'), description: error.message, variant: 'destructive' });
    }
  };

  const handleDeleteTemplate = async () => {
    if (!deletingTemplate) return;
    try {
      await supabase.from('expense_templates').delete().eq('id', deletingTemplate.id);
      toast({ description: t('templates.removed', { name: deletingTemplate.name }) });
      loadData();
    } catch (error) {
      toast({ title: t('common.error'), description: t('templates.deleteFailed'), variant: 'destructive' });
    } finally {
      setDeleteTemplateOpen(false);
      setDeletingTemplate(null);
    }
  };

  const handleAddClick = () => {
    if (activeTab === 'bills') {
      handleAddRecurring();
    } else {
      setTemplateDialogOpen(true);
    }
  };

  const currencySymbol = CURRENCY_SYMBOLS[currency as keyof typeof CURRENCY_SYMBOLS] || currency;
  const activeExpenses = expenses.filter(e => e.is_active);
  const monthlyTotal = calculateMonthlyTotal(expenses);

  const getCategoryEmoji = (category: string): string => {
    const map: Record<string, string> = {
      'Food': '🍔',
      'Transport': '🚌',
      'Shopping': '🛍️',
      'Fun': '🎮',
      'Health': '💊',
      'Housing': '🏠',
      'Bills/Other': '📄',
    };
    return map[category] || '📦';
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <CreditCard className="h-5 w-5 text-primary" />
          {t('savedExpenses.title')}
        </h3>
        <Button size="sm" onClick={handleAddClick}>
          <Plus className="h-4 w-4 mr-1" />
          {activeTab === 'bills' ? t('savedExpenses.addBill') : t('savedExpenses.addTemplate')}
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="bills" className="text-sm">
            {t('savedExpenses.billsTab')}
          </TabsTrigger>
          <TabsTrigger value="quickadd" className="text-sm">
            {t('savedExpenses.quickAddTab')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="bills" className="mt-4">
          {isLoading ? (
            <Card><CardContent className="p-4"><div className="animate-pulse space-y-3"><div className="h-4 bg-muted rounded w-1/2" /><div className="h-4 bg-muted rounded w-3/4" /></div></CardContent></Card>
          ) : activeExpenses.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="p-6 text-center">
                <p className="text-muted-foreground mb-1">
                  💳 {t('savedExpenses.emptyBills')}
                </p>
                <p className="text-sm text-muted-foreground mb-4">
                  {t('savedExpenses.emptyBillsHint')}
                </p>
                <Button onClick={handleAddRecurring}>
                  <Plus className="h-4 w-4 mr-2" />
                  {t('savedExpenses.addBill')}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {activeExpenses.map((expense) => (
                <Card key={expense.id} className="overflow-hidden">
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <span className="text-xl flex-shrink-0">{getCategoryIcon(expense.category)}</span>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium truncate">{expense.name}</span>
                            {expense.is_variable_amount && <Badge variant="outline" className="text-xs">~</Badge>}
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <span className={getDueUrgencyClass(expense.next_due_date)}>{getDueDateLabel(expense.next_due_date)}</span>
                            <span className="text-muted-foreground">·</span>
                            <span className="text-muted-foreground">{expense.is_auto_paid ? 'Auto' : 'Manual'}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <div className="text-right">
                          <span className="font-semibold">{expense.is_variable_amount && '~'}{currencySymbol}{expense.amount.toFixed(2)}</span>
                          <span className="text-xs text-muted-foreground block">{getFrequencyShort(expense.frequency)}</span>
                        </div>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEditRecurring(expense)}><Pencil className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => { setDeletingExpense(expense); setDeleteRecurringOpen(true); }}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              <div className="pt-2 border-t mt-3">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-muted-foreground">Monthly total (approx.)</span>
                  <span className="font-semibold">~{currencySymbol}{monthlyTotal.toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="quickadd" className="mt-4">
          {isLoading ? (
            <Card><CardContent className="p-4"><div className="animate-pulse space-y-3"><div className="h-4 bg-muted rounded w-1/2" /><div className="h-4 bg-muted rounded w-3/4" /></div></CardContent></Card>
          ) : templates.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="p-6 text-center">
                <p className="text-muted-foreground mb-1">
                  ☕ {t('savedExpenses.emptyQuickAdd')}
                </p>
                <p className="text-sm text-muted-foreground mb-4">
                  {t('savedExpenses.emptyQuickAddHint')}
                </p>
                <Button onClick={() => setTemplateDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  {t('savedExpenses.addTemplate')}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {templates.map((template) => (
                <Card key={template.id} className="overflow-hidden">
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <span className="text-xl flex-shrink-0">{getCategoryEmoji(template.category)}</span>
                        <div className="min-w-0 flex-1">
                          <span className="font-medium truncate block">{template.name}</span>
                          <span className="text-sm text-muted-foreground">{template.merchant}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="font-semibold">{currencySymbol}{template.amount.toFixed(2)}</span>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => { setDeletingTemplate(template); setDeleteTemplateOpen(true); }}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Recurring Expense Dialog */}
      <RecurringExpenseDialog
        open={recurringDialogOpen}
        onOpenChange={setRecurringDialogOpen}
        expense={editingExpense}
        onSaved={loadData}
        currency={currency}
      />

      {/* Template Create Dialog */}
      <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('templates.create')}</DialogTitle>
            <DialogDescription>{t('templates.createDescription')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label htmlFor="template-name">{t('templates.templateName')}</Label>
              <Input id="template-name" placeholder={t('templates.namePlaceholder')} value={newTemplate.name} onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="template-amount">{t('expense.amount')}</Label>
                <Input id="template-amount" type="number" step="0.01" placeholder="4.50" value={newTemplate.amount} onChange={(e) => setNewTemplate({ ...newTemplate, amount: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="template-currency">{t('expense.currency')}</Label>
                <Select value={newTemplate.currency} onValueChange={(value) => setNewTemplate({ ...newTemplate, currency: value })}>
                  <SelectTrigger id="template-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>{CURRENCIES.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="template-merchant">{t('expense.merchant')}</Label>
              <Input id="template-merchant" placeholder={t('templates.merchantPlaceholder')} value={newTemplate.merchant} onChange={(e) => setNewTemplate({ ...newTemplate, merchant: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="template-category">{t('expense.category')}</Label>
              <Select value={newTemplate.category} onValueChange={(value) => setNewTemplate({ ...newTemplate, category: value })}>
                <SelectTrigger id="template-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((cat) => (<SelectItem key={cat} value={cat}>{cat}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="template-note">{t('expense.noteOptional')}</Label>
              <Input id="template-note" placeholder={t('templates.notePlaceholder')} value={newTemplate.note} onChange={(e) => setNewTemplate({ ...newTemplate, note: e.target.value })} />
            </div>
            <Button onClick={handleCreateTemplate} disabled={!newTemplate.name || !newTemplate.amount || !newTemplate.merchant} className="w-full">{t('templates.createButton')}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Recurring Confirmation */}
      <AlertDialog open={deleteRecurringOpen} onOpenChange={setDeleteRecurringOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove recurring expense?</AlertDialogTitle>
            <AlertDialogDescription>Remove "{deletingExpense?.name}" from your bills?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteRecurring} className="bg-destructive hover:bg-destructive/90">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Template Confirmation */}
      <AlertDialog open={deleteTemplateOpen} onOpenChange={setDeleteTemplateOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove template?</AlertDialogTitle>
            <AlertDialogDescription>Remove "{deletingTemplate?.name}" from your quick add list?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteTemplate} className="bg-destructive hover:bg-destructive/90">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
